import { Color, Vector3 } from 'three';
import type { Object3D, Texture } from 'three';
import type { MapDef } from '@cloudpearl/config';
import type { DeathCause, World } from '@cloudpearl/core';
import { PEARL_RADIUS, PearlRig } from '@cloudpearl/assets';
import type { PearlMode } from '@cloudpearl/assets';
import type { Bursts } from '../world/Particles';
import type { TrailMap } from '../world/TrailMap';
import { P } from '../world/Particles';

const _v = new Vector3();
const _w = new Vector3();
const _n = new Vector3();
const _c = new Color();
const UP = new Vector3(0, 1, 0);

/**
 * What a view needs to know about a pearl. The local `Sim` satisfies it directly; a friend's
 * pearl in a shared run (`RemotePearl`) fills it from network snapshots.
 */
export interface PearlState {
  readonly world: World;
  readonly started: boolean;
  readonly alive: boolean;
  readonly cause: DeathCause | null;
  readonly grounded: boolean;
  readonly holding: boolean;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly prevX: number;
  readonly prevY: number;
  readonly prevZ: number;
  readonly vx: number;
  readonly vy: number;
  readonly vz: number;
  readonly nx: number;
  readonly ny: number;
  readonly nz: number;
  readonly speed: number;
  readonly flow: number;
}

/**
 * Puts a pearl where its state says (interpolated between fixed ticks), smooths its heading
 * for the camera, drives the rig (roll, squash) and presses its trail into the sand and grass.
 */
export class PlayerView {
  readonly rig: PearlRig;
  /** Contact point (bottom of the pearl). */
  readonly position = new Vector3();
  readonly velocity = new Vector3();
  /** Smoothed horizontal heading. */
  readonly forward = new Vector3(0, 0, -1);
  readonly right = new Vector3(1, 0, 0);
  airHeight = 0;
  private dustTimer = 0;
  private tumble: { p: Vector3; v: Vector3; t: number } | null = null;
  private readonly lastContact = new Vector3();
  private hasContact = false;
  private readonly trail: TrailMap | null;
  private readonly colors: { dust: Color; spark: Color; sand: Color; ground: Color };
  private readonly map: MapDef;

  constructor(parent: Object3D, map: MapDef, trail: TrailMap | null, tint?: string) {
    this.map = map;
    this.trail = trail;
    this.rig = new PearlRig();
    if (tint) this.rig.tint(tint);
    parent.add(this.rig.root);
    this.colors = {
      dust: new Color(map.terrain.style === 'dune' ? map.sand.lit : '#efe6d2').lerp(new Color(map.palette.fog), 0.25),
      spark: new Color('#fff1d6'),
      sand: new Color(map.sand.lit),
      ground: new Color(map.terrain.style === 'dune' ? map.sand.lit : map.grass.tip),
    };
  }

  setEnvMap(env: Texture | null): void {
    this.rig.setEnvMap(env);
  }

  /** Recompute the world transform from interpolated sim state. */
  sync(sim: PearlState, alpha: number, dt: number, time: number, bursts: Bursts): void {
    const w = sim.world;
    if (this.tumble) {
      const tb = this.tumble;
      tb.t += dt;
      tb.v.y -= 26 * dt;
      tb.p.addScaledVector(tb.v, dt);
      const g = w.height(tb.p.x, tb.p.z);
      if (w.mask(tb.p.x, tb.p.z) > 0.5 && tb.p.y < g) {
        tb.p.y = g;
        if (tb.v.y < -2) {
          bursts.spray(time, 4, P.Puff, tb.p, UP, 1.2, [1, 3], [0.5, 0.9], [0.3, 0.5, 2.2], this.colors.dust, 0.05, 0.8);
          this.rig.impulse(Math.min(1, -tb.v.y * 0.08));
        }
        tb.v.y = Math.abs(tb.v.y) * 0.4;
        // Settle instead of micro-bouncing every frame (the crash camera is watching it).
        if (tb.v.y < 1.2) tb.v.y = 0;
        tb.v.x *= 0.7;
        tb.v.z *= 0.7;
      }
      if (w.mask(tb.p.x, tb.p.z) > 0.5 && tb.p.y <= g + 0.01 && tb.v.y <= 0) {
        // Rolling to a stop on the ground.
        const k = Math.exp(-dt * 2.5);
        tb.v.x *= k;
        tb.v.z *= k;
      }
      this.position.copy(tb.p);
      this.velocity.copy(tb.v);
    } else {
      this.position.set(sim.prevX + (sim.x - sim.prevX) * alpha, sim.prevY + (sim.y - sim.prevY) * alpha, sim.prevZ + (sim.z - sim.prevZ) * alpha);
      this.velocity.set(sim.vx, sim.vy, sim.vz);
    }
    this.airHeight = Math.max(0, this.position.y - w.height(this.position.x, this.position.z));

    // Smoothed heading: follows horizontal velocity, faster the quicker we go.
    const hs = Math.hypot(this.velocity.x, this.velocity.z);
    if (sim.started && hs > 1.5 && !this.tumble) {
      _v.set(this.velocity.x / hs, 0, this.velocity.z / hs);
      const k = 1 - Math.exp(-dt * (3 + Math.min(hs, 60) * 0.12));
      this.forward.lerp(_v, k).normalize();
    } else if (!sim.started) {
      this.forward.set(0, 0, -1);
    }
    this.right.set(-this.forward.z, 0, this.forward.x);

    let mode: PearlMode;
    if (!sim.started) mode = 'idle';
    else if (!sim.alive) mode = sim.cause === 'fall' ? 'fall' : 'crash';
    else if (sim.grounded) mode = sim.holding ? 'dive' : 'roll';
    else mode = 'air';
    _n.set(sim.nx, sim.ny, sim.nz);
    this.rig.root.position.copy(this.position);
    // Resting or rolling, the pearl's weight settles it into the sand (grass hides its base).
    const onGround = !this.tumble && (sim.grounded || !sim.started);
    if (onGround && this.map.terrain.style === 'dune') this.rig.root.position.y -= 0.08;
    this.rig.update({ mode, speed: sim.speed, flow: sim.flow, time, velocity: this.velocity, normal: _n }, dt);

    // At rest (menu), it sits in a little dent it has pressed into the sand / turf.
    if (this.trail && !sim.started) {
      const dune = this.map.terrain.style === 'dune';
      this.trail.stamp(this.position.x, this.position.z, this.position.x, this.position.z, dune ? 0.78 : 0.62, dune ? 0.9 : 0.55);
    }
    // The trail: press a groove into the sand / lay the grass down along the path.
    const contact = sim.started && sim.alive && sim.grounded && !this.tumble;
    if (this.trail && contact) {
      const jump = this.hasContact ? Math.hypot(this.position.x - this.lastContact.x, this.position.z - this.lastContact.z) : 99;
      const from = jump < 6 ? this.lastContact : this.position;
      this.trail.stamp(from.x, from.z, this.position.x, this.position.z, sim.holding ? 0.58 : 0.5, sim.holding ? 1 : 0.85);
      this.lastContact.copy(this.position);
      this.hasContact = true;
    } else if (!contact) this.hasContact = false;

    if (sim.started && sim.alive && dt > 0) {
      const sp = sim.speed;
      // Dunes: a soft plume of sand dust drifting off behind the pearl (Journey's sand wake).
      if (this.map.terrain.style === 'dune' && sim.grounded && sp > 9) {
        this.dustTimer -= dt * (10 + sp * 0.6) * (sim.holding ? 1.5 : 1);
        while (this.dustTimer < 0) {
          this.dustTimer += 1;
          // Low and streaming: kicked back off the pearl, carried sideways by the wind, hugging the
          // ground (never a rising column).
          _v.copy(this.position).addScaledVector(this.forward, -0.5 - Math.random() * 0.5);
          _v.y += 0.3;
          _w.copy(this.forward).multiplyScalar(-(1.5 + Math.min(sp, 60) * 0.12) * (0.6 + Math.random() * 0.4));
          _w.x += this.windX * 3.5 + (Math.random() - 0.5) * 1.6;
          _w.z += this.windZ * 3.5 + (Math.random() - 0.5) * 1.6;
          _w.y = 0.15 + Math.random() * 0.3;
          bursts.emit(time, P.Dust, _v, _w, 1.1 + Math.random() * 0.7, 0.22 + Math.random() * 0.12, 0.75 + sp * 0.006, this.colors.dust, 0.18);
        }
      }
      // Ground contact: sand spray or grass flecks while diving.
      if (sim.grounded && sp > 10) {
        const dune = this.map.terrain.style === 'dune';
        if (Math.random() < dt * (sim.holding ? 40 : 14)) {
          _v.copy(this.position).addScaledVector(this.forward, -0.4);
          _w.copy(this.forward).multiplyScalar(-0.5).add(UP).normalize();
          if (dune) bursts.spray(time, sim.holding ? 6 : 3, P.Grain, _v, _w, 0.9, [3, 8 + sp * 0.08], [0.3, 0.7], [0.014, 0.026, 0.6], this.colors.sand, 0.12, 0.65);
          else bursts.spray(time, sim.holding ? 2 : 1, P.Petal, _v, _w, 1.0, [2, 5], [0.5, 0.9], [0.05, 0.08, 0.8], this.colors.ground, 0.15);
        }
      }
      if (sim.flow >= 0.6 && Math.random() < dt * 10) {
        _v.copy(this.position).add(_w.set((Math.random() - 0.5) * 1.4, PEARL_RADIUS + (Math.random() - 0.5) * 1.2, (Math.random() - 0.5) * 1.4));
        _c.setHSL(0.55 + Math.random() * 0.35, 0.8, 0.8);
        bursts.emit(time, P.Spark, _v, _w.set(0, 0.6, 0).addScaledVector(this.velocity, 0.3), 0.6, 0.06, 0.01, _c, 0.7);
      }
    }
  }

  land(quality: 'perfect' | 'good' | 'rough', impact: number, time: number, bursts: Bursts): void {
    this.rig.impulse(0.25 + impact * 0.7);
    this.trail?.stamp(this.position.x, this.position.z, this.position.x, this.position.z, 0.75 + impact * 0.45, 1);
    const dune = this.map.terrain.style === 'dune';
    const n = Math.round(6 + impact * 14);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      _w.set(Math.cos(a), 0.25, Math.sin(a)).multiplyScalar(2 + impact * 5);
      _c.copy(this.colors.dust).multiplyScalar(0.92 + Math.random() * 0.12);
      bursts.emit(time, P.Puff, this.position, _w, 0.6 + Math.random() * 0.4, 0.2, 0.6 + impact * 0.6, _c, 0.5);
    }
    if (dune) bursts.spray(time, 20, P.Grain, this.position, UP, 1.6, [3, 9], [0.5, 1], [0.016, 0.03, 0.6], this.colors.sand, 0.15, 0.7);
    else bursts.spray(time, 10, P.Petal, this.position, UP, 1.8, [3, 7], [0.7, 1.2], [0.05, 0.09, 0.9], this.colors.ground, 0.2);
    if (quality === 'perfect') {
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2;
        _w.set(Math.cos(a) * 5, 1.2 + Math.random() * 1.5, Math.sin(a) * 5);
        _c.setHSL(0.1 + Math.random() * 0.05, 0.5, 0.85);
        bursts.emit(time, P.Spark, this.position, _w, 0.5, 0.12, 0.02, _c, 0.8);
      }
    }
  }

  /** Threaded a wind gate: a ring of sparkles. */
  gate(time: number, bursts: Bursts): void {
    for (let i = 0; i < 26; i++) {
      const a = (i / 26) * Math.PI * 2;
      _w.copy(this.right).multiplyScalar(Math.cos(a) * 7).add(_v.set(0, Math.sin(a) * 7, 0)).addScaledVector(this.velocity, 0.6);
      _c.setHSL(0.55 + Math.random() * 0.1, 0.5, 0.85);
      bursts.emit(time, P.Spark, this.position, _w, 0.7, 0.16, 0.02, _c, 0.9);
    }
  }

  closeCall(side: -1 | 1, time: number, bursts: Bursts): void {
    _v.copy(this.position).addScaledVector(this.right, side * 1.2).add(_w.set(0, 0.8, 0));
    for (let i = 0; i < 6; i++) {
      _w.copy(this.forward).multiplyScalar(-18 - Math.random() * 10).addScaledVector(this.right, side * 2).add(_n.set(0, Math.random() * 2 - 1, 0));
      bursts.emit(time, P.Streak, _v, _w, 0.35, 0.12, 0.05, _c.set('#ffffff'), 0.8);
    }
  }

  crash(sim: PearlState, material: 'stone' | 'wood' | 'plant' | 'none', time: number, bursts: Bursts): void {
    const knock = material === 'plant' ? 0.5 : 1;
    const hs = Math.hypot(sim.vx, sim.vz) || 1;
    this.tumble = {
      p: this.position.clone(),
      v: new Vector3((-sim.vx / hs) * Math.min(hs, 40) * 0.18 * knock + (Math.random() - 0.5) * 4, 7 + Math.random() * 3, (-sim.vz / hs) * Math.min(hs, 40) * 0.18 * knock + (Math.random() - 0.5) * 4),
      t: 0,
    };
    this.rig.impulse(1.2);
    bursts.spray(time, 18, P.Puff, this.position, UP, 2.2, [2, 7], [0.6, 1.2], [0.4, 0.7, 2.6], this.colors.dust, 0.06, 0.6);
    const debris = material === 'stone' ? '#9a98a8' : material === 'wood' ? '#7c5a48' : this.map.palette.leafB;
    bursts.spray(time, 16, P.Petal, this.position, UP, 2.4, [4, 10], [0.8, 1.4], [0.08, 0.14, 1], _c.set(debris), 0.2);
    for (let i = 0; i < 10; i++) {
      _w.set((Math.random() - 0.5) * 10, Math.random() * 6, (Math.random() - 0.5) * 10);
      _c.setHSL(0.1, 0.4, 0.85);
      bursts.emit(time, P.Spark, this.position, _w, 0.5, 0.14, 0.02, _c, 0.8);
    }
  }

  caught(): void {
    this.tumble = { p: this.position.clone(), v: new Vector3((Math.random() - 0.5) * 3, 9, 6), t: 0 };
  }

  reset(): void {
    this.tumble = null;
    this.hasContact = false;
    this.forward.set(0, 0, -1);
    this.right.set(1, 0, 0);
    this.rig.reset();
  }

  dispose(): void {
    this.rig.root.removeFromParent();
    this.rig.dispose();
  }

  private windX = 0;
  private windZ = 0;
  /** Wind direction for drifting dust (set by the host from the map). */
  setWind(x: number, z: number): void {
    this.windX = x;
    this.windZ = z;
  }
}
